import type { ApiResponse, ServiceOptions } from '@/types/core/api'
import type { MockTokenResponse, UserRole, RoleUser, AuthUser, RoleInfo } from '@/types/auth'
import { MENU_CONFIG } from '@/types/auth'
import { ApiService } from '@/services/core/ApiService'
import { logger } from '@/utils/logger'
import { useAuthStore } from '@/stores/authStore'

export interface RoleDefinition {
  role: UserRole
  name: string
  permissions: string[]
}

class AuthService extends ApiService {
  constructor() {
    super('/api/auth')
  }

  /**
   * 取得可用角色列表
   */
  async getAvailableRoles(options?: ServiceOptions<RoleDefinition[]>): Promise<ApiResponse<RoleDefinition[]>> {
    const endpoint = '/roles'
    const response = await this.get<RoleDefinition[]>(endpoint, undefined, options)
    return response
  }

  /**
   * 取得指定角色的可用使用者列表
   */
  async getRoleUsers(role: UserRole, options?: ServiceOptions<RoleUser[]>): Promise<ApiResponse<RoleUser[]>> {
    const endpoint = `/roles/${role}/users`
    
    // 在開發模式下，嘗試從 localStorage 取得 channelId 作為 query parameter
    let params: any = undefined
    const isDev = import.meta.env.MODE === 'development'
    
    if (isDev) {
      // 首先嘗試從 localStorage 取得 channelId（主要方式）
      let channelId = localStorage.getItem('selected_channel_id')
      
      // 如果沒有找到，嘗試從 sessionStorage 的完整 channel 物件中解析（備用方式）
      if (!channelId) {
        const saved = sessionStorage.getItem('selected_channel')
        if (saved) {
          try {
            const channelInfo = JSON.parse(saved)
            channelId = channelInfo.channel_id
          } catch (error) {
            logger.warn('Failed to parse selected_channel from sessionStorage', { error })
          }
        }
      }
      
      if (channelId) {
        params = { channelId }
        logger.debug('Adding channelId to getRoleUsers request', {
          role,
          channelId,
          source: localStorage.getItem('selected_channel_id') ? 'localStorage' : 'sessionStorage'
        })
      }
    }
    
    const response = await this.get<RoleUser[]>(endpoint, params, options)
    return response
  }

  /**
   * 取得模擬 token（私有方法，統一處理 token 請求）
   */
  private async getMockToken(role: UserRole, userId?: string, options?: ServiceOptions<MockTokenResponse>): Promise<MockTokenResponse> {
    const endpoint = userId ? `/mock-token/${role}/${userId}` : `/mock-token/${role}`
    const response = await this.get<MockTokenResponse>(endpoint, undefined, options)
    return response.data
  }

  /**
   * 儲存 token 到 localStorage
   */
  setToken(token: string): void {
    localStorage.setItem('auth_token', token)
  }

  /**
   * 從 localStorage 取得 token
   */
  getToken(): string | null {
    return localStorage.getItem('auth_token')
  }

  /**
   * 移除 token
   */
  removeToken(): void {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user') // 同時移除用戶資訊
  }

  /**
   * 儲存用戶資訊到 localStorage
   */
  setUser(user: AuthUser): void {
    localStorage.setItem('auth_user', JSON.stringify(user))
  }

  /**
   * 從 localStorage 取得用戶資訊
   */
  getUser(): AuthUser | null {
    const userStr = localStorage.getItem('auth_user')
    if (!userStr) return null
    try {
      return JSON.parse(userStr)
    } catch (error) {
      logger.error('Failed to parse user info', { error })
      return null
    }
  }

  /**
   * 登入並儲存 token 和用戶資訊
   */
  async login(role: UserRole, userId?: string, options?: ServiceOptions<MockTokenResponse>): Promise<{ token: string; user: AuthUser }> {
    const response = await this.getMockToken(role, userId, options)
    
    this.setToken(response.token)
    
    // 處理 JWT 並建立前端 AuthUser
    const processedUser = this.processLoginResponse(response)
    this.setUser(processedUser)
    
    return {
      token: response.token,
      user: processedUser
    }
  }

  /**
   * 處理 LINE OAuth 回調
   */
  async processLineCallback(callbackData: any, options?: ServiceOptions<any>): Promise<ApiResponse<{ token: string; user: AuthUser; needRegister?: boolean }>> {
    logger.auth('PROCESSING_LINE_CALLBACK', {
      hasCode: !!callbackData.code,
      hasState: !!callbackData.state,
      hasChannelId: !!callbackData.channelId,
      devMode: callbackData.devMode,
      codeLength: callbackData.code ? callbackData.code.length : 0
    })
    
    const response = await this.post('/line/callback', callbackData, options)
    
    logger.auth('LINE_CALLBACK_RESPONSE_RECEIVED', {
      success: response.success,
      hasData: !!response.data,
      hasToken: response.data?.token ? response.data.token.length > 0 : false,
      hasUser: !!response.data?.user,
      isExist: response.data?.isexist
    })
    
    // 檢查是否需要註冊 (isexist = false)
    if (response.success && response.data && response.data.isexist === false) {
      logger.auth('USER_NEEDS_REGISTRATION', {
        display_name: response.data.display_name,
        lineid: response.data.lineid,
        channelid: response.data.channelid
      })
      
      // 將 LINE 資料暫存到 store 中供註冊頁面使用
      const authStore = useAuthStore()
      authStore.setTempLineData({
        display_name: response.data.display_name,
        avatar: response.data.avatar,
        lineid: response.data.lineid,
        token: response.data.token,
        channelid: response.data.channelid,
        orgid: response.data.orgid
      })
      
      // 回傳需要註冊的訊息，包含 LINE 資料
      return {
        success: true,
        code: 'NEED_REGISTER',
        message: '需要註冊',
        data: {
          token: response.data.token,
          user: {} as AuthUser,
          needRegister: true,
          // 包含 LINE 資料供前端使用
          display_name: response.data.display_name,
          avatar: response.data.avatar,
          lineid: response.data.lineid,
          channelid: response.data.channelid,
          orgid: response.data.orgid,
          isexist: response.data.isexist
        }
      }
    }
    
    // 如果回調成功且使用者已存在，儲存 token 和用戶資訊
    if (response.success && response.data && response.data.isexist !== false) {
      logger.auth('STORING_LINE_CALLBACK_RESULTS', {
        tokenLength: response.data.token ? response.data.token.length : 0,
        userId: response.data.user?.id,
        userRoleCount: response.data.user?.roles?.length || 0
      })
      
      this.setToken(response.data.token)
      
      // 處理 JWT 並建立前端 AuthUser，保留來自活動平台的 username
      const processedUser = this.processLoginResponse({
        token: response.data.token,
        user: {
          ...response.data.user,
          username: response.data.user.username // 確保 username 被保留
        }
      })
      this.setUser(processedUser)
      
      // 更新 response 中的 user 資訊
      response.data.user = processedUser
      
      logger.auth('LINE_LOGIN_COMPLETED_SUCCESSFULLY', {
        userId: processedUser.id,
        currentRoleType: processedUser.currentRole?.type,
        totalRoles: processedUser.roles?.length,
        channelId: processedUser.channelId
      })
    } else {
      logger.error('LINE_CALLBACK_FAILED', {
        success: response.success,
        message: response.message,
        code: response.code
      })
    }
    
    return response
  }

  /**
   * 解析 JWT token 取得 payload
   */
  parseToken(token: string): any {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      logger.error('Failed to parse token', { error })
      return null
    }
  }

  /**
   * 檢查 token 是否過期
   */
  isTokenExpired(token?: string): boolean {
    const authToken = token ?? this.getToken()
    if (!authToken) return true

    const payload = this.parseToken(authToken)
    if (!payload?.exp) return true

    const now = Math.floor(Date.now() / 1000)
    return payload.exp < now
  }

  /**
   * 取得當前使用者資訊
   */
  getCurrentUser(): AuthUser | null {
    const token = this.getToken()
    if (!token || this.isTokenExpired(token)) {
      return null
    }
    
    const user = this.getUser()
    if (!user) return null
    
    // 如果沒有頭像，生成一個預設的頭像
    if (!user.avatar && user.name) {
      user.avatar = this.generateDefaultAvatar(user.name, user.id)
    }
    
    return user
  }

  /**
   * 處理登入回應，從 JWT 建立前端 AuthUser
   */
  private processLoginResponse(response: MockTokenResponse): AuthUser {
    const jwtPayload = this.parseToken(response.token)
    
    // 從JWT取得角色列表
    const roles: RoleInfo[] = jwtPayload.roles || []
    
    // 選擇預設角色（優先順序：vendor > admin > channel_admin > user）
    const currentRole = this.selectDefaultRole(roles)
    
    // 如果是 vendor 角色，設定 currentVendorId
    const currentVendorId = currentRole.type === 'vendor' && currentRole.vendorId 
      ? currentRole.vendorId 
      : undefined
    
    return {
      id: jwtPayload.sub,
      name: response.user.name,
      username: response.user.username,
      email: response.user.email,
      roles: roles,
      currentRole: currentRole,
      currentVendorId: currentVendorId,
      channelId: jwtPayload.channelId,
      avatar: response.user.avatar
    }
  }

  /**
   * 選擇預設角色
   */
  private selectDefaultRole(roles: RoleInfo[]): RoleInfo {
    // 優先順序：vendor > admin > channel_admin > user
    for (const priority of ['vendor', 'admin', 'channel_admin', 'user']) {
      const role = roles.find(r => r.type === priority)
      if (role) return role
    }
    
    // 如果都沒有，回傳第一個角色
    return roles[0] || { type: 'user', role: 'user' }
  }

  /**
   * 切換角色（純前端操作）
   */
  switchRole(newRole: RoleInfo): boolean {
    const user = this.getCurrentUser()
    if (!user) return false

    // 檢查是否為有效角色
    const hasRole = user.roles.some(role => 
      role.type === newRole.type && 
      role.role === newRole.role && 
      role.vendorId === newRole.vendorId
    )

    if (!hasRole) return false

    // 更新當前角色
    user.currentRole = newRole
    
    // 如果切換到 vendor 角色，設定 currentVendorId
    if (newRole.type === 'vendor' && newRole.vendorId) {
      user.currentVendorId = newRole.vendorId
    } else {
      user.currentVendorId = undefined
    }
    
    this.setUser(user)
    
    // 觸發更新事件
    window.dispatchEvent(new CustomEvent('userUpdated', { detail: user }))
    
    return true
  }

  /**
   * 生成預設頭像 URL
   */
  private generateDefaultAvatar(name: string, userId: string): string {
    // 使用 ui-avatars.com 服務生成頭像
    const initial = name.charAt(0).toUpperCase()
    const colors = ['3B82F6', '10B981', 'F59E0B', 'EF4444', '8B5CF6', 'F97316', '06B6D4', 'EC4899']
    const colorIndex = userId.charCodeAt(0) % colors.length
    const backgroundColor = colors[colorIndex]
    
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initial)}&background=${backgroundColor}&color=ffffff&size=128&font-size=0.6`
  }

  /**
   * 基於 MENU_CONFIG 檢查使用者是否可以訪問特定路由
   */
  canAccessRoute(route: string): boolean {
    const user = this.getCurrentUser()
    if (!user?.currentRole) return false

    const userRole = user.currentRole.role as UserRole

    // 開發模式的角色選擇頁面，所有登入用戶都可以訪問
    const devRoutes = ['/admin/mock-user-selection', '/mock-user-selection']
    if (devRoutes.some(r => route.startsWith(r))) return true

    // 基本路由所有登入使用者都可以訪問
    const publicRoutes = ['/dashboard', '/user', '/vendors', '/exchange', '/notifications']
    if (publicRoutes.some(r => route.startsWith(r))) return true

    // 檢查 MENU_CONFIG 中是否有匹配的路由
    for (const section of MENU_CONFIG) {
      // 檢查使用者是否可以訪問該選單區塊
      if (!section.roles.includes(userRole)) continue

      // 檢查該區塊下的選單項目
      for (const item of section.items) {
        if (item.roles.includes(userRole) && route.startsWith(item.route)) {
          return true
        }
      }
    }

    return false
  }

  /**
   * 取得使用者可訪問的所有路由
   */
  getUserAllowedRoutes(): string[] {
    const user = this.getCurrentUser()
    if (!user?.currentRole) return []

    const userRole = user.currentRole.role as UserRole
    const allowedRoutes: string[] = []

    // 添加基本路由
    allowedRoutes.push('/dashboard', '/user', '/vendors', '/exchange', '/notifications')

    // 從 MENU_CONFIG 收集可訪問的路由
    for (const section of MENU_CONFIG) {
      if (section.roles.includes(userRole)) {
        for (const item of section.items) {
          if (item.roles.includes(userRole)) {
            allowedRoutes.push(item.route)
          }
        }
      }
    }

    return allowedRoutes
  }

  /**
   * 發起真實 LINE Login (生產環境)
   * @param channelId LINE Channel ID (從選擇的主辦單位取得)
   * @param isAdmin 是否為管理員登入
   */
  initiateRealLineLogin(channelId: string, isAdmin: boolean = false): void {
    const loginType = isAdmin ? 'ADMIN' : 'USER'
    
    logger.auth(`INITIATING_REAL_LINE_LOGIN_${loginType}`, {
      isAdmin,
      channelId,
      hasChannelId: !!channelId
    })
    
    // 驗證必要的參數
    if (!channelId) {
      logger.error('channelId is required for LINE OAuth')
      throw new Error('請先選擇主辦單位')
    }
    
    // 動態組裝 redirect URI
    const redirectUri = `${window.location.origin}/line/callback`
    
    // 儲存登入來源
    sessionStorage.setItem('login_source', isAdmin ? 'admin' : 'user')
    
    // 生成安全的 state 參數
    const state = this.generateSecureState()
    sessionStorage.setItem('oauth_state', state)
    
    logger.auth('OAUTH_STATE_GENERATED', { 
      stateLength: state.length,
      statePrefix: state.substring(0, 8) + '...'
    })
    
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: channelId,
      redirect_uri: redirectUri,
      state: state,
      scope: 'profile openid'
      // 移除 prompt: 'consent' 以避免顯示驗證許可提示
      // 如果使用者已經授權過，LINE 會自動跳過授權畫面
    })
    
    const lineOAuthUrl = `https://access.line.me/oauth2/v2.1/authorize?${params}`
    
    logger.auth('REDIRECTING_TO_LINE_OAUTH_URL', { 
      urlLength: lineOAuthUrl.length,
      domain: 'access.line.me',
      paramCount: params.toString().split('&').length,
      channelId,
      redirectUri
    })
    
    window.location.href = lineOAuthUrl
  }

  /**
   * 生成安全的隨機 state 參數
   */
  private generateSecureState(): string {
    const array = new Uint8Array(32)
    crypto.getRandomValues(array)
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
  }

  /**
   * 檢查使用者是否具有店家權限
   */
  hasVendorPermission(): boolean {
    const user = this.getCurrentUser()
    if (!user?.currentRole) return false
    
    const userRole = user.currentRole.role as UserRole
    return userRole === 'vendor' || userRole === 'vendor_co'
  }

  /**
   * 檢查使用者是否已選擇店家身份
   */
  hasSelectedVendor(): boolean {
    const user = this.getCurrentUser()
    return !!(user?.currentVendorId)
  }

  /**
   * 檢查使用者是否可以訪問店家功能
   */
  canAccessVendorFeatures(): boolean {
    return this.hasVendorPermission() && this.hasSelectedVendor()
  }

  /**
   * 根據使用者角色取得預設首頁路由
   */
  getDefaultRoute(): string {
    const user = this.getCurrentUser()
    if (!user?.currentRole) return '/login'

    const userRole = user.currentRole.role as UserRole

    // 根據角色優先順序決定預設頁面
    switch (userRole) {
      case 'super_admin':
      case 'super_admin_co':
        return '/admin/super-admin/channels'
      case 'channel_admin':
        return '/admin/channel/products/review'
      case 'vendor':
      case 'vendor_co':
        return '/admin/vendor/products'
      case 'user':
        return '/admin/vendor-application'
      default:
        return '/dashboard'
    }
  }

}

export const authService = new AuthService()